import React from 'react';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
  formButtonList,
} from '@/src/strategies/util';
import Type from 'src/components/Modules-revamp/CarouselType';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import Animation from 'src/components/Modules-revamp/Animation';
import ModuleWidth from 'src/components/Modules-revamp/ModuleWidthSelect';
import AutoPlay from '@/src/components/Modules-revamp/AutoPlay';
import Products from '@/src/components/Modules-revamp/Products';
import productCarouselRenderTemplate from 'src/strategies/render/productCarousel';
import { ContentModuleGroupPreloadingContext } from 'src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import { get } from 'lodash';
import moduleStyleRender from '../../ModuleStyle';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import { Field } from 'rc-field-form';
import { useProductCarousel } from '@/src/constrains/SalesChannelContent-Revamp/hooks/useProductCarousel';
import ProductsToShow from '@/src/components/Modules-revamp/ItemToShow';
import Text from '@/src/components/Modules-revamp/Text';
import CallToActionButtonTheme from '@/src/components/Modules-revamp/CallToActionButtonTheme';
import LeaveGapBetweenProducts from '@/src/components/Modules-revamp/LeaveGapBetween';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
};

const productCarouselRenderer = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const {
    handleOnSectionReset,
    handleOnSectionCopy,
    salesChannelMode,
    disabled,
  } = extra;
  const list = productCarouselRenderTemplate(
    values,
    breakpoint,
    salesChannelMode,
  );
  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f, disabled));
    } else {
      return field.map((f) => productCarouselComponent(f, breakpoint, extra));
    }
  };
  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={get(item, 'validator')}
              disabled={disabled}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const productCarouselComponent = (
  data: ResponsiveFieldProps,
  breakpoint: EPreviewSize,
  extra: any,
) => {
  const { animations, hoverAnimations, form, buttons, disabled } = extra || {};
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  const { onSort, onDelete, onReselect } = extra;
  switch (data.field) {
    case ResponsiveField.CarouselType: {
      return (
        <Type
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.AutoPlay: {
      return <AutoPlay {...data} disabled={data?.disabled || disabled} />;
    }
    case ResponsiveField.ProductToShow: {
      return (
        <ProductsToShow
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          options={data?.options || []}
          disabled={data?.disabled || disabled}
        />
      );
    }
    case ResponsiveField.LeaveGap: {
      return (
        <LeaveGapBetweenProducts
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          options={data?.options || []}
          disabled={data?.disabled || disabled}
        />
      );
    }
    case ResponsiveField.CallToActionButton: {
      return (
        <CallToActionButtonTheme
          options={formButtonList(buttons)}
          disabled={data?.disabled || disabled}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled || disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.Products: {
      return (
        <Products
          draggableId={draggableId}
          disabled={data?.disabled || disabled}
          moduleType={EnumModuleType.ProductCarousel}
          breakpoint={breakpoint}
          onSort={onSort}
          onDelete={onDelete}
          onReselect={onReselect}
          disableDeleteText={data?.disabled}
        />
      );
    }
    case ResponsiveField.ProductText: {
      return (
        <Text
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          disabled={data?.disabled || disabled}
        />
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }: any) => {
  const { animations, hoverAnimations, buttons } = React.useContext(
    ContentModuleGroupPreloadingContext,
  );
  const { handleOnDelete, handleOnSort, handleOnReselect } =
    useProductCarousel();
  return (
    <Field shouldUpdate>
      {() =>
        productCarouselRenderer(form, breakpoint, {
          animations,
          buttons,
          hoverAnimations,
          form,
          onSort: handleOnSort,
          onDelete: handleOnDelete,
          onReselect: handleOnReselect,
          ...extra,
        })
      }
    </Field>
  );
};
